import { ReactComponent as LoadingDataGraph } from 'components/icons/graph-loadingData.svg';
import { CardBackground } from 'components/ui';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store';
import { useGetUserInfoQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { greetingMessage } from 'utils/helpers';
import {
  OnboardingStatus,
  useUserOnboardingStatus,
} from 'utils/useUserOnboardingStatus';
import { ExploreSegments } from './ExploreSegments';
import { HelpPrompt } from './HelpPrompt';

export default function WelcomePage() {
  const { currentStore, loggedInUser } = useAppSelector(userDetailsState);

  const userOnboarding = useUserOnboardingStatus();

  const { data: userInfo } = useGetUserInfoQuery();

  const hasObjectives = userInfo?.has_objectives;

  const estimateTime = (percentage: number) => {
    if (percentage < 10) {
      return '6 hours';
    }
    if (percentage < 30) {
      return '5 hours';
    }
    if (percentage < 50) {
      return '4 hours';
    }
    if (percentage < 70) {
      return '3 hours';
    }
    if (percentage < 90) {
      return '2 hours';
    }
    if (percentage < 100) {
      return '1 hour';
    }
    if (percentage === 100) {
      return 'Your data is ready';
    }
  };

  return (
    <div className="relative max-w-screen-xl px-2.5 mx-auto md:pl-segments-72px md:pr-20 py-11">
      <p className="font-bold leading-6">Dashboard</p>
      <h1 className="mt-4 text-4xl font-bold leading-10">
        {greetingMessage()}, {loggedInUser?.split(' ')[0]}
      </h1>
      {!!!hasObjectives && (
        <div className="mt-10">
          <SetupSegmentsPrompt />
        </div>
      )}
      <div className="mt-10">
        <CardBackground>
          <div className="lg:grid lg:grid-cols-12 lg:pl-[42px] lg:pr-7 lg:h-[321px] px-5">
            {userOnboarding.status === OnboardingStatus.COMPLETED ? (
              <div className="flex flex-col justify-between col-span-7 py-10">
                <div>
                  <p className="text-2xl leading-7 ">Your data is ready</p>
                  <p className="mt-2 text-black text-opacity-50 text-segments-13px lg:text-base">
                    Uncover hidden data insights, take action with segmentation,
                    and grow your business.
                  </p>
                </div>
                <div>
                  {!!!hasObjectives && (
                    <p className="mb-6 text-black text-opacity-50">
                      Looks like you haven't set up Segments yet, let's start
                      from there.
                    </p>
                  )}
                  <Link
                    className="lg:w-[284px] h-[52px] bg-segments-accent-color-1 rounded-xl flex justify-center items-center text-white font-bold mt-4 lg:mt-0 w-full"
                    to={{
                      pathname: hasObjectives ? '/dashboard' : '/onboarding',
                      search:
                        currentStore.serializedStoreName + '&welcome=true',
                    }}
                  >
                    Start exploring
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col justify-between col-span-7 py-10">
                <div>
                  <p className="text-2xl leading-7 ">
                    We are getting your data ready
                  </p>
                  <p className="mt-2 text-black text-opacity-50 text-segments-13px lg:text-base">
                    Please allow up to a few hours as we ingest all your
                    historical Shopify data and build out your analytics.{' '}
                  </p>
                </div>
                <div className="mt-10 lg:mt-0">
                  <p className="text-black text-segments-13px text-opacity-30">
                    {userOnboarding.percentage}% | estimated time left:{' '}
                    {estimateTime(userOnboarding.percentage)}
                  </p>
                  <div className="relative h-1 mt-2.5">
                    <div className="absolute w-full h-full bg-gray-200" />
                    <motion.div
                      initial={{ width: '0' }}
                      animate={{
                        width: `${userOnboarding.percentage}%`,
                      }}
                      transition={{ duration: 3 }}
                      className="relative h-full font-bold rounded-full bg-segments-accent-color-1"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-end justify-end w-full h-full col-span-5">
              <div className="w-[360px]  ">
                <LoadingDataGraph />
              </div>
            </div>
          </div>
        </CardBackground>
      </div>

      <div className="w-full my-10 space-y-10 lg:grid lg:items-start lg:grid-cols-2 lg:gap-x-8 lg:space-y-0">
        <ExploreSegments />
        <HelpPrompt />
      </div>
    </div>
  );
}

function SetupSegmentsPrompt() {
  const { currentStore } = useAppSelector(userDetailsState);
  return (
    <div className="px-5 py-5 shadow-sm lg:items-center lg:justify-between lg:flex lg:p-10 bg-segments-accent-green-3 rounded-2xl">
      <div className="lg:space-x-6 lg:items-center lg:flex">
        <div className="flex items-center justify-center w-20 h-20 p-4 pl-6 bg-white rounded-full">
          <HammerIcon />
        </div>
        <div className="mt-6 lg:mt-0">
          <p className="text-2xl leading-7">Set up your Segments</p>
          <p className="mt-2 text-base text-black text-opacity-50">
            Get your Segments ready in a few simple steps.
          </p>
        </div>
      </div>
      <div className="flex justify-end mt-10 lg:mt-0">
        <Link
          to={{
            pathname: '/onboarding',
            search: currentStore.serializedStoreName,
          }}
          className="px-14 bg-segments-accent-color-1 text-white font-bold py-3.5 rounded-2xl"
        >
          Start
        </Link>
      </div>
    </div>
  );
}

function HammerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 42 50">
      <path
        fill="#151415"
        stroke="#020202"
        strokeMiterlimit="10"
        d="M17.006 3.27h6.8c5.216 0 14.987 7.82 14.987 7.82H36.02s-7.923-3.91-12.082-3.91H16.94V3.27h.066z"
      ></path>
      <path
        fill="#fff"
        stroke="#151415"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        d="M19.317 1.667h6.8c5.216 0 14.987 7.82 14.987 7.82h-2.773s-7.923-3.91-12.082-3.91H19.25v-3.91h.066z"
      ></path>
      <path fill="#FF9650" d="M20.307 11.09H14.43v18.718h5.876V11.09z"></path>
      <path
        fill="#fff"
        stroke="#151415"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        d="M19.052 11.859h-4.885c-1.123 0-1.981-.897-1.981-1.923V2.244c0-1.026.858-1.923 1.98-1.923h4.952c1.717 0 3.037 1.346 3.037 2.948v5.577c0 1.667-1.386 3.013-3.103 3.013z"
      ></path>
      <path
        fill="#151415"
        stroke="#020202"
        strokeMiterlimit="10"
        d="M12.582 11.859c.693 0 1.254-2.583 1.254-5.77 0-3.185-.561-5.768-1.254-5.768s-1.254 2.583-1.254 5.769.561 5.769 1.254 5.769z"
      ></path>
      <path
        fill="#fff"
        stroke="#151415"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        d="M12.186 2.436h-4.16v7.692h4.16V2.436z"
      ></path>
      <path
        fill="#fff"
        stroke="#151415"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        d="M5.584 12.692H3.537c-.99 0-1.783-.769-1.783-1.73v-9.36c0-.705.595-1.281 1.32-1.281h2.51C7.432.32 8.95 1.795 8.95 3.59v5.833c.066 1.795-1.453 3.27-3.367 3.27z"
      ></path>
      <path
        fill="#151415"
        stroke="#020202"
        strokeMiterlimit="10"
        d="M2.085 12.757c.692 0 1.254-2.784 1.254-6.218C3.339 3.104 2.777.32 2.085.32 1.392.32.83 3.104.83 6.539c0 3.434.562 6.218 1.255 6.218z"
      ></path>
      <path
        fill="#151415"
        d="M17.336 50c-2.641 0-4.754-2.051-4.754-4.615V28.462c0-2.564 2.113-4.616 4.754-4.616 2.64 0 4.753 2.052 4.753 4.616v16.923C22.155 47.949 19.977 50 17.336 50z"
      ></path>
      <path
        stroke="#151415"
        strokeMiterlimit="10"
        d="M17.336 9.936h4.622m-3.302-.833h3.301"
      ></path>
      <path stroke="#fff" strokeMiterlimit="10" d="M20.307 27.628V49.36"></path>
    </svg>
  );
}
